/**
 * col-equal-form (US-AIF.P0-0239-S02 · @UC9413) — FORM COL-EQUAL (thu đều) THUẦN.
 *
 * VERTICAL SLICE mẫu của AI form protocol: usecase "thu đều mỗi người X". Định nghĩa CHUỖI FIELD
 * tất-định + validate CẢ BỘ trước khi confirm gọi createAdhoc. Ngồi TRÊN form-slot-filling (S03 của
 * 0237 — validate 1 value + field kế) và collection-rules (assertAdhocAmountPositive/assertDueDateNotPast).
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now (trừ `now` inject cho test date-not-past).
 * Money = string minor VND (B1 — no-float, >0). fund_picker options do SERVICE nạp (quỹ THẬT của user)
 * — domain chỉ validate value ∈ options (chống IDOR quỹ lạ ở LỚP domain, ngoài RBAC service).
 *
 * Chuỗi field (thứ tự hỏi — tất-định): [fund_picker(fundId) → text(title) → money(amountPerMember) →
 * date(dueDate)]. MỌI field required (không optional chặn confirm).
 */
import { DomainError } from "../errors.js";
import {
  assertAdhocAmountPositive,
  assertDueDateNotPast,
} from "../adhoc-collection.js";
import { DEFAULT_GROUP_TIMEZONE } from "../collection-schedule.js";
import { validateFieldValue, type SlotFieldSpec, type SlotOption, type FormState } from "./form-slot-filling.js";

/** intentCode COL-EQUAL (khớp router DEFAULT_COLLECT_INTENT_CODE + FORM_SPEC_CATALOG). */
export const COL_EQUAL_INTENT_CODE = "COL-EQUAL";

/** specVersion form COL-EQUAL (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const COL_EQUAL_SPEC_VERSION = 1;

/**
 * 🔴 C3 (SEC MED-1) — Trần PG signed BIGINT. amount > trần ⇒ ghi DB ném P2020 → 500. Domain chốt
 * TRƯỚC ⇒ 400 (DomainError) thay vì crash. `MONEY_MINOR_REGEX` (form-slot-filling) cho tới 19 chữ số
 * nên "9999999999999999999" (19 số 9) LỌT regex nhưng VƯỢT trần ⇒ cần chốt giá trị THẬT ở đây.
 *
 * ⚠️ NGUỒN SỰ THẬT: `ADHOC_PG_BIGINT_MAX` ở `@goppy/contracts` (packages/contracts/src/collections.ts:52).
 * @goppy/domain KHÔNG phụ thuộc @goppy/contracts (giữ domain thuần — xem package.json) ⇒ NHÂN BẢN literal
 * y hệt ở đây, cùng comment tham chiếu để hai bên KHÔNG lệch. Đổi một bên PHẢI đổi bên kia.
 */
export const COL_EQUAL_PG_BIGINT_MAX = 9223372036854775807n;

/**
 * 🔴 H1 — Trần độ dài tiêu đề. HTTP contract cap `.max(100)` (ADHOC_TITLE_MAX_LENGTH). Đường AI BYPASS
 * schema HTTP ⇒ phải chốt cùng ngưỡng, nếu không title >100 crash ở DB (varchar) hoặc ghi rác.
 *
 * ⚠️ NGUỒN SỰ THẬT: `ADHOC_TITLE_MAX_LENGTH` ở `@goppy/contracts` (packages/contracts/src/collections.ts:42).
 * Domain thuần (không import contracts) ⇒ NHÂN BẢN literal + comment tham chiếu để hai bên KHÔNG lệch.
 */
export const COL_EQUAL_TITLE_MAX_LENGTH = 100;

/** Thứ tự field COL-EQUAL (tất-định — thứ tự hỏi). Test khoá thứ tự này (contract nghiệp vụ). */
export const COL_EQUAL_FIELD_ORDER = [
  "fundId",
  "title",
  "amountPerMember",
  "dueDate",
] as const;

/** Payload đã validate — nối THẲNG vào CreateAdhocInput (fundId/title/amountMinor/dueDate). */
export interface ColEqualPayload {
  readonly fundId: string;
  readonly title: string;
  /** STRING số nguyên dương minor (B1 — service parse → BigInt). */
  readonly amountMinor: string;
  /** 'YYYY-MM-DD'. */
  readonly dueDate: string;
}

/**
 * Dựng chuỗi field COL-EQUAL với options quỹ THẬT nạp vào fund_picker (service query quỹ user active).
 * Immutable: KHÔNG mutate `fundOptions` input (copy sang mảng options mới). MỌI field required.
 */
export function buildColEqualFields(
  fundOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    { key: "amountPerMember", type: "money", required: true },
    { key: "dueDate", type: "date", required: true },
  ];
}

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Thiếu thông tin bắt buộc: ${field}.`,
    );
  }
  return value.trim();
}

/**
 * Validate CẢ BỘ formState COL-EQUAL → ColEqualPayload chuẩn hoá (fail-fast, KHÔNG partial).
 *
 * Thứ tự chốt (tất-định):
 *  1. fundId có + ∈ options (chống IDOR quỹ lạ ở LỚP domain — service còn re-verify RBAC thật).
 *  2. title non-empty (trim).
 *  3. amountPerMember money minor no-float (validateFieldValue) + >0 (assertAdhocAmountPositive BigInt).
 *  4. dueDate ISO hợp lệ (validateFieldValue) + KHÔNG quá khứ (assertDueDateNotPast theo tz group).
 *
 * Ném DomainError (map 400) cho MỌI vi phạm — KHÔNG ghi dữ liệu rác. `now` inject cho test.
 */
export function validateColEqualForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColEqualPayload {
  // 1) fundId ∈ options (domain-level IDOR guard — quỹ KHÔNG thuộc danh sách user ⇒ reject).
  const fundIdRaw = assertPresent(formState.fundId, "fundId");
  const allowedFundIds = new Set(fundOptions.map((o) => o.value));
  if (!allowedFundIds.has(fundIdRaw)) {
    // Không có mã lỗi riêng cho "quỹ lạ" — dùng REQUIRED_FIELD_MISSING (fundId hợp lệ CÒN THIẾU).
    // Đây LỚP defense-in-depth: RBAC/IDOR THẬT do service re-verify (NotFoundException 404 đồng nhất).
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Quỹ được chọn không nằm trong danh sách quỹ của bạn.",
    );
  }

  // 2) title non-empty + ≤ trần độ dài (H1 — mirror HTTP .max(100), chống over-long crash/rác DB).
  const title = assertPresent(formState.title, "title");
  if (title.length > COL_EQUAL_TITLE_MAX_LENGTH) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tiêu đề quá dài (tối đa ${COL_EQUAL_TITLE_MAX_LENGTH} ký tự).`,
    );
  }

  // 3) amount money minor no-float + >0 (B1). validateFieldValue chốt regex minor; domain chốt >0.
  const amountRaw = assertPresent(formState.amountPerMember, "amountPerMember");
  const amountValidation = validateFieldValue(
    { key: "amountPerMember", type: "money", required: true },
    amountRaw,
  );
  if (!amountValidation.ok) {
    throw new DomainError("COLLECTION_AMOUNT_NOT_POSITIVE", amountValidation.message);
  }
  // assertAdhocAmountPositive ném DomainError khi ≤0 (0/âm đã qua regex minor? "0" hợp regex → chốt ở đây).
  assertAdhocAmountPositive(BigInt(amountValidation.value));
  // 🔴 C3 (SEC MED-1) — chốt TRẦN BIGINT: "9999999999999999999" lọt regex minor nhưng vượt PG BIGINT
  // ⇒ ghi DB P2020 → 500. Chốt ở domain ⇒ DomainError (400) thay vì crash. Value đã qua regex minor
  // (chỉ chữ số) nên BigInt() KHÔNG ném — so sánh an toàn.
  if (BigInt(amountValidation.value) > COL_EQUAL_PG_BIGINT_MAX) {
    throw new DomainError(
      "COLLECTION_AMOUNT_NOT_POSITIVE",
      "Số tiền vượt quá giới hạn cho phép.",
    );
  }
  const amountMinor = amountValidation.value;

  // 4) dueDate ISO hợp lệ + KHÔNG quá khứ (tz group).
  const dueDateRaw = assertPresent(formState.dueDate, "dueDate");
  const dateValidation = validateFieldValue(
    { key: "dueDate", type: "date", required: true },
    dueDateRaw,
  );
  if (!dateValidation.ok) {
    throw new DomainError("COLLECTION_DUE_DATE_IN_PAST", dateValidation.message);
  }
  assertDueDateNotPast(parseIsoDateUtc(dateValidation.value), DEFAULT_GROUP_TIMEZONE, now);

  return { fundId: fundIdRaw, title, amountMinor, dueDate: dateValidation.value };
}

/** Parse 'YYYY-MM-DD' (đã validate ISO) → Date UTC midnight (ổn định, không phụ thuộc tz máy). */
function parseIsoDateUtc(isoDate: string): Date {
  const [y, m, d] = isoDate.split("-").map((p) => Number.parseInt(p, 10));
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
}
